/**
 * @module Experince/Product-Selector
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */
/*tslint:disable:max-file-line-count */
/* tslint:disable:no-any */ // Need to support all data types
// Core Libraries

import { Injectable } from '@angular/core';
import { throwError as observableThrowError, Observable, Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { Router } from '@angular/router';

// Dependent Services
import { ContentProviderService } from '@core/aem/services/content-provider.service';
import { ApiGatewayService } from '@core/api/services/api-gateway-service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { CartSupportService } from '@support/cart-support/services/cart-support.service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';

// Interfaces
import { SharedBrowseService } from '@experience/shop/shared-browse/services/shared-browse.service';
import { BrowseEntry } from '@experience/shop/shared-browse/interfaces/browse-entry.interface';
import { AuditRecords } from '@support/api/proxies/responses/api-2116.interface';
import {
  LineDetailsInfo,
  LineConfig,
} from '@experience/shop/product-selector/interfaces/product-selector.interface';
import { ImagesData } from '@experience/shop/shared-browse/components/product-carousel/models/product-carousel.interface';
import { EligibleServices, Services } from '@support/api/proxies/responses/api-2102.interface';
import { PayLoadServiceRequest } from '@support/api/proxies/requests/api-2102.interface';
import { APIError } from '@support/api/common/error.interface';
import { LineDeivceDetails } from '@experience/shop/product-selector/interfaces/product-selector.interface';

// Constants
import { API_MAP } from '@core/api/proxies/common-api-endpoints';
import { PRODUCT_SELECTOR_CONSTANTS } from '@experience/shop/product-selector/constants/product-selector.constants';
import {
  ByodObject,
  ExistingSimData,
} from '@experience/shop/byod/interfaces/byod-landing.interface';
import {
  CartItem,
  Cart,
  CartLine,
} from '@support/api/proxies/responses/api-cart-response.interface';
import { SHARED_BROWSE_CONSTANTS } from '@experience/shop/shared-browse/constants/shared-browse.constants';
import { ORDER_TYPE } from '@experience/shop/shop.constants';
import { ServiceSelectRequestBodyInterface } from '@experience/shop/browse-services/interfaces/services.interface';
// import { SETTINGS, CHANNELS } from '@environment/settings';
import { CartUtils } from '@support/cart-support/cart-utils';
import { CART_ITEM_STATUS } from '@support/cart-support/constants/cart-support-service-constants';
import { EndpointConfigInterface } from '@core/interfaces/endpoint-configuration.interface';
import { TileManagementService } from '@support/shared/services/tile-management.service';
import { ConfigData } from '@experience/repdash/interfaces/aem-content.interface';
import { HttpResponse, HttpHeaders } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class ProductSelectorService {
  /**
   * Subscription source - allows multiple observers to receive broadcasted changes
   */

  public productSelectorAddedAccessory: boolean;

  public tileInFocusUpdated$: Observable<boolean>;
  /**
   * Subject declaration - will be cast as observable, allows multiple subscribers
   */
  private tileInFocusUpdatedSource: Subject<boolean>;

  /* change route selection event*/
  public changeRouteSelectionSource: Subject<string>;
  /* change route selection listener*/
  public changeRouteSelection$: Observable<string>;

  constructor(
    private contentProviderService: ContentProviderService,
    private apiGateway: ApiGatewayService,
    private router: Router,
    private sharedBrowseService: SharedBrowseService,
    private cartSupportService: CartSupportService,
    private stateStoreUtilsService: StateStoreUtilsService,
    private objectService: ObjectService,
    private tileManagementService: TileManagementService
  ) {
    // Instantate subject and cast as observable
    this.tileInFocusUpdatedSource = new Subject();
    this.tileInFocusUpdated$ = this.tileInFocusUpdatedSource.asObservable();
    this.changeRouteSelectionSource = new Subject();
    this.changeRouteSelection$ = this.changeRouteSelectionSource.asObservable();
  }

  /**
   * Retrieves active line tiles inside stateStoreUtilsService
   */
  public getActiveTiles(): string[] {
    return this.stateStoreUtilsService.getActiveTiles();
  }

  /**
   * Triggers event to change route selection
   */
  public changeRouteSelection(familyId: string): void {
    this.changeRouteSelectionSource.next(familyId);
  }

  /**
   * Sets active line tiles inside stateStoreUtilsService
   * @returns void
   */
  public setActiveTiles(activeTiles: string[]): void {
    this.stateStoreUtilsService.setActiveTiles(activeTiles);
  }

  /**
   * Retrieves the cart data currently stored in stateStoreUtilsService
   * @returns Cart
   */
  public getCurrentCart(): Cart {
    return this.stateStoreUtilsService.getCart();
  }

  /**
   * Sets Active line in focus
   * @param lineId active line
   * @returns void
   */
  public setTileIdInFocus(tileId: string): void {
    this.announceTileInFocusUpdate(tileId);
  }

  /**
   * Provides update when new lineId is in focus
   * @param newTileIdInFocus: new lindId in focus
   * @return void
   */
  public announceTileInFocusUpdate(newTileIdInFocus: string): void {
    this.stateStoreUtilsService.setTileIdInFocus(newTileIdInFocus);
    this.tileInFocusUpdatedSource.next(true);
  }

  /**
   * Model to Restore BYOD Data
   * @returns ByodObject
   */
  public getByodRestoreModel(): ByodObject {
    return this.stateStoreUtilsService.getByodRestoreModel();
  }

  /**
   * Get API call to get Eligible Services
   * @returns Observable<Services>
   */
  public getEligibleServices(nested?: boolean): Observable<Services> {
    const isUpgrade = this.stateStoreUtilsService.getUpgradeFlow();
    const custId = this.stateStoreUtilsService.getBscsCustomerID();
    const customerConfigData: BrowseEntry = this.sharedBrowseService.getUserInputsConfigData();
    const zip =
      this.objectService.get(this.stateStoreUtilsService.getBillingAddress(), 'postalCode') ||
      this.objectService.get(customerConfigData, 'zip') ||
      this.stateStoreUtilsService.getStoreZipCode();
    let headers = new HttpHeaders();
    headers = headers.append('billingType', this.stateStoreUtilsService.getBillingTypeOfCustomerInFocus());
    const payLoadServiceRequest: PayLoadServiceRequest = {
      lineId: !isUpgrade
        ? this.stateStoreUtilsService.getFocusedTileId()
        : this.objectService.get(
            this.stateStoreUtilsService.getFocusedTileLineNonLineItem(),
            'plansFromCart.id'
          ),
      customerId: this.stateStoreUtilsService.getBscsCustomerID() || '',
      accountId: this.stateStoreUtilsService.getAddFan()
        ? ''
        : this.stateStoreUtilsService.getAccountId(),
      dealerCode: this.stateStoreUtilsService.getDealerCode()
        ? this.stateStoreUtilsService.getDealerCode().toString()
        : '',
      transactionTypes: !isUpgrade
        ? custId
          ? [ORDER_TYPE.ADDALINE]
          : [PRODUCT_SELECTOR_CONSTANTS.ACTIVATION]
        : [ORDER_TYPE.PURCHASEEQUIPMENT],
    };
    const pathParams = {
      cartId: this.stateStoreUtilsService.getCartId(),
      lineId: payLoadServiceRequest.lineId,
    };
    const queryParams = {
      nested: nested ? 'TRUE' : 'FALSE',
      zipcode: zip,
    };
    return this.apiGateway
      .postRequest(
        API_MAP.API_2102_retrieveservices,
        payLoadServiceRequest,
        headers,
        queryParams,
        pathParams
      )
      .pipe(
        map((response: HttpResponse<Services>) => {
          return response ? response.body : null;
        }),
        catchError((error: APIError[]) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * Post API call to get Eligible Services
   * @returns Observable<EligibleServices>
   */
  public postEligibleServices(
    id: string,
    offerId: string,
    isUpgrade?: boolean,
    nested?: boolean
  ): Observable<EligibleServices> {
    const eligibleServicesPayload: ServiceSelectRequestBodyInterface = {
      id,
      offerId,
      customerId: this.stateStoreUtilsService.getBscsCustomerID() || '',
    };
    const queryParams = {
      nested: nested ? 'TRUE' : 'FALSE',
    };
    const lineId = !isUpgrade
      ? this.stateStoreUtilsService.getFocusedTileId()
      : this.objectService.get(
          this.stateStoreUtilsService.getFocusedTileLineNonLineItem(),
          'plansFromCart.id'
        );
    const pathParams = {
      cartId: this.stateStoreUtilsService.getCartId(),
      lineId: lineId,
    };
    return this.apiGateway
      .putRequest(
        API_MAP.API_2113_postServiceSelection,
        eligibleServicesPayload,
        null,
        queryParams,
        pathParams
      )
      .pipe(
        map((response: HttpResponse<EligibleServices>) => {
          return response ? response.body : null;
        }),
        catchError((error: APIError[]) => {
          return observableThrowError(error);
        })
      );
  }
  /**
   * Function navigates back to entry point from selected route
   * @returns void
   */
  public navigageToEntryPoint(): void {
    const tileIdInFocus = this.stateStoreUtilsService.getTileIdInFocus();
    const tileModel = this.stateStoreUtilsService.getTileModelForTileId(tileIdInFocus);
    const isUpgrade = tileModel && tileModel.isDeviceUpgrade ? true : false;
    if (
      isUpgrade &&
      this.stateStoreUtilsService.getEntryPoint() === PRODUCT_SELECTOR_CONSTANTS.DEVICE_FIRST
    ) {
      this.router.navigate(['/product-details'], { queryParams: { edit: true } });
    } else {
      switch (this.stateStoreUtilsService.getEntryPoint()) {
        case PRODUCT_SELECTOR_CONSTANTS.PLAN_FIRST:
          this.router.navigate(['browse-plans-phone']);
          break;
        case PRODUCT_SELECTOR_CONSTANTS.BYOD_FIRST:
          this.router.navigate(['bring-your-own-device']);
          break;
        default:
          this.router.navigate(['browse-phones']);
          break;
      }
    }
  }

  /**
   * Method to get Number of Lines present in Cart
   * @returns number
   */
  public getCartMaxLines(): number {
    return this.stateStoreUtilsService.getMaxLineDetails().maxLines;
  }

  /**
   * Method to get Max Number of allowed Voice lines
   * @returns number
   */
  public getMaxVoiceLines(): number {
    return this.stateStoreUtilsService.getMaxLineDetails().maxVoiceLines;
  }

  /**
   * Method to set Max Number of allowed voice lines
   * @returns number
   */
  public getMaxTabletLines(): number {
    return this.stateStoreUtilsService.getMaxLineDetails().maxTabletLines;
  }

  /**
   * Get AEM Content for Product Selector Landing component
   * @returns Observable<object>
   */
  public getProductSelectorContent(): Observable<Object> {
    this.cartSupportService.announceProductSelectorSpinnerUpdate(true);
    return this.contentProviderService.getContentData('product-selector').pipe(
      map((contentData: Object) => {
        const aemData = {
          configurableData: null,
          authorableData: null,
        };
        this.cartSupportService.announceProductSelectorSpinnerUpdate(false);
        aemData.configurableData = <ConfigData>(
          this.contentProviderService.getConfigurableData(contentData)
        );
        aemData.authorableData = this.contentProviderService.getAuthorableData(contentData) || [];
        return aemData;
      })
    );
  }

  /**
   * Method to add new line
   * @returns void
   */
  public addNewTile(): void {
    const activeTiles = this.stateStoreUtilsService.getActiveTiles() || [];
    const tempTileId = SHARED_BROWSE_CONSTANTS.NEW_LINE_ADDED;
    if (activeTiles.indexOf(tempTileId) === -1) {
      activeTiles.push(tempTileId);
      this.stateStoreUtilsService.setActiveTiles(activeTiles);
      this.announceTileInFocusUpdate(tempTileId);
    }
  }
  /**
   * Method to get Lines data
   * @returns CartLine[]
   */
  public getCreatedLines(): string[] {
    const lineArray = [];
    const lineLength = this.objectService.get(
      this.stateStoreUtilsService.getCart(),
      'lines.length'
    );
    for (let line = 0; line <= lineLength - 1; line++) {
      lineArray.push('line'.concat(line.toString()));
    }
    return lineArray;
  }

  /**
   * Method to get Line Configuration Info
   * @param tiles Selected Line
   * @returns LineDetailsInfo
   */
  public getLineConfigurationInfo(tiles: string[]): LineDetailsInfo {
    const cart = this.stateStoreUtilsService.getCart();
    const output: LineDetailsInfo = {
      lineConfig: [],
      unConfiguredLinesCount: 0,
      tabletLines: 0,
      voiceLines: 0,
    };
    output.unConfiguredLinesCount = tiles.length - cart.lines.length;
    const lines = cart.lines;
    tiles.forEach((tile: string, index: number) => {
      const line = this.getLineById(lines, index);
      const isDeviceSelected =
        line &&
        this.objectService.get(line, 'device') &&
        this.objectService.get(line, 'device.length ') > 0;
      const isPlanSelected =
        line &&
        this.objectService.get(line, 'plan') &&
        this.objectService.get(line, 'plan.length ') > 0;
      const lineType = line ? this.objectService.get(line, 'lineType') : '';
      output.lineConfig.push({
        isDeviceSelected: isDeviceSelected,
        isPlanSelected: isPlanSelected,
        planType: lineType,
        deviceDescription: isDeviceSelected ? this.objectService.get(line, 'device.offerName') : '',
        planDescriptions: isPlanSelected ? this.objectService.get(line, 'plan.description') : '',
      });
      output.tabletLines = lineType === 'TABLET' ? output.tabletLines + 1 : output.tabletLines;
      output.voiceLines = lineType === 'HANDSET' ? output.voiceLines + 1 : output.voiceLines;
    });
    // output.tabletLines = this.getMaxTabletLines() - output.tabletLines;
    // output.voiceLines = this.getMaxVoiceLines() - output.voiceLines;
    return output;
  }

  /**
   * Method to enable or disable cart button
   * @returns boolean
   */
  public getLineDetails(lineDetails: LineDetailsInfo): boolean {
    return (
      lineDetails.lineConfig.filter((item: LineConfig) => {
        return !(
          this.objectService.get(item, 'isDeviceSelected') &&
          this.objectService.get(item, 'isPlanSelected')
        );
      }).length > 0
    );
  }

  /**
   * Retrieves Existing Accessories count
   * @param sku SKU Details
   * @returns number
   */
  public getExistingAccessoryCount(sku: string): number {
    const cart: Cart = this.stateStoreUtilsService.getCart();
    const accessoriesArr = cart.accessories.filter((accessory: CartItem) => {
      return this.objectService.get(accessory, 'upc')
        ? this.objectService.get(accessory, 'upc').toString() === sku.toString()
        : false;
    });
    if (accessoriesArr.length) {
      return this.objectService.get(accessoriesArr, '0.quantity');
    } else {
      return 0;
    }
  }

  /**
   * API call to get Accessories
   * @returns Observable<ImagesData>
   */
  public getAccessories(offerId: string, url: EndpointConfigInterface): Observable<ImagesData> {
    const headers = new HttpHeaders();
    const sortCriterion = {
      sortDirection: 'ascending',
      sortOptionValue: 'relevance',
    };
    const pathParams = { productId: offerId };
    // let retrieveStoreId: string;
    // if (SETTINGS.CHANNEL.toLowerCase() === CHANNELS.RETAIL) {
    //   retrieveStoreId = this.stateStoreUtilsService.getStoreId();
    // }
    const data = {
      productTypes: ['ACCESSORIES'],
      productSubTypes: [''],
      accountId: this.stateStoreUtilsService.getAccountId(),
      lineOfServiceId: '',
      billingType: this.stateStoreUtilsService.getCustomerBillingType()
        ? this.stateStoreUtilsService.getCustomerBillingType
        : '',
      customerId: this.stateStoreUtilsService.getCustomerId(),
      customerSegments: '',
      accountSegments: [''],
      dealerCodes: [''],
      masterDealerCodes: [''],
      transactionTypes: ['ACTIVATION'],
      postalCode: this.stateStoreUtilsService.getCustomerConfigData().zip,
      // storeId: retrieveStoreId ? retrieveStoreId : '',
      storeId: '',
      offerTypes: ['HARDGOODS'],
      offerSubTypes: ['BASE'],
      sortOptions: sortCriterion,
      pageSize: '20',
      pageNumber: '',
      creditLevels: [''],
      tacCodes: [''],
      relationTypes: [''],
      productFamilyIds: [''],
      accessoryCategory: '',
      requestedSkus: [''],
      facets: [''],
      channelSegment: {
        salesChannelCode: '',
        salesSubChannelCode: '',
        subChannelCategory: '',
      },
      filtersRequested: ['all'],
      filterOptionsSelected: [
        {
          fieldName: 'all',
          values: ['all'],
        },
      ],
      isDetailRequest: true,
    };
    return this.apiGateway.postRequest(url, data, headers, null, pathParams).pipe(
      map((response: HttpResponse<Object>) => {
        return response.body;
      }),
      catchError((error: APIError) => {
        return observableThrowError(error);
      })
    );
  }

  /**
   * method to check Accerssories present
   * @returns boolean
   */
  public getAccessoryCount(): boolean {
    const cart: Cart = this.stateStoreUtilsService.getCart();
    let accessoriesArr: number = 0;
    if (cart && this.objectService.get(cart, 'accessories')) {
      accessoriesArr = cart.accessories.length;
    }
    return accessoriesArr > 0 ? true : false;
  }

  /**
   * Check all lines in cart have a plan selected. If not then return true to disable AddLineCTA
   * @returns boolean
   */
  public canDisableAddLineIfAllLinesDoNotHavePlan(): boolean {
    let notAllLinesInCartHavePlan = false;
    const activeTiles = this.stateStoreUtilsService.getActiveTiles();
    for (const tileId of activeTiles) {
      const tileModel = this.stateStoreUtilsService.getTileModelForTileId(tileId);
      if (this.objectService.get(tileModel, 'noPlan')) {
        // Non-Line Item need to check if tile has device selected
        if (!this.objectService.get(tileModel, 'deviceId')) {
          notAllLinesInCartHavePlan = true;
        }
      } else {
        // Line item, need to check if line has plan selected
        const cartLine = this.cartSupportService.getLineFromCart(
          this.objectService.get(tileModel, 'lineId')
        );
        const planDetails = cartLine
          ? CartUtils.getCartItemAsArrayFromLine(cartLine, CART_ITEM_STATUS.ITEM_ADD, 'plan')
          : null;
        if (!planDetails) {
          notAllLinesInCartHavePlan = true;
        }
      }
    }
    return notAllLinesInCartHavePlan;
  }

  /**
   * This method post the user phone number, Account number, pin
   * If it's a device, then make the API call to add the device as a new line item
   * @param formData, lineId, controlName
   */
  public postAuditRecords(formData: ExistingSimData, nickname?: string): Observable<AuditRecords> {
    const lineNumber: string = this.tileManagementService.getTileIdInFocus();
    const auditRecordRequest = [];
    for (const itemField in formData) {
      if (itemField) {
        auditRecordRequest.push({
          'audit-key': itemField,
          'audit-value': formData[itemField],
        });
      }
    }
    auditRecordRequest.push({
      'audit-key': 'nickName',
      'audit-value': nickname,
    });
    auditRecordRequest.push({
      'audit-key': 'lineNumber',
      'audit-value': lineNumber,
    });
    const headers: HttpHeaders = new HttpHeaders();
    const pathParams = {
      cartId: this.stateStoreUtilsService.getCartId(),
      lineId: lineNumber,
    };
    const auditContextRequest = {
      auditContextRecords: [
        {
          auditContext: PRODUCT_SELECTOR_CONSTANTS.AUDIT_NICKNAME,
          auditRecords: auditRecordRequest,
        },
      ],
    };
    const auditRequest = {
      auditRecords: auditRecordRequest,
    };
    const requestPayload = nickname.length > 0 ? auditContextRequest : auditRequest;
    return this.apiGateway
      .postRequest(API_MAP.API_2116_postAuditRecords, requestPayload, headers, null, pathParams)
      .pipe(
        map((response: HttpResponse<AuditRecords>) => {
          return response.body;
        }),
        catchError((error: APIError) => {
          return observableThrowError(error);
        })
      );
  }
  /**
   * Function to group the devices with same SKU's
   * @param CartItem[], string
   * @returns CartItem[]
   */
  public groupSimilarSkuDevices(products: LineDeivceDetails[], type: string): LineDeivceDetails[] {
    const tempSkuArr = [];
    const similarSkuProducts = [];
    if (type === PRODUCT_SELECTOR_CONSTANTS.ACCESSORIES) {
      return products;
    } else if (products) {
      for (const device of products) {
        if (tempSkuArr.indexOf(this.objectService.get(device, 'selectedDevice.sku')) < 0) {
          tempSkuArr.push(this.objectService.get(device, 'selectedDevice.sku'));
        }
      }

      const skuArrLength = tempSkuArr.length;
      for (let i = 0; i < skuArrLength; i++) {
        const skuProducts = this.getFilteredProducts(products, tempSkuArr[i]);
        skuProducts[0].quantity = skuProducts.length;
        similarSkuProducts.push(skuProducts[0]);
      }
      return similarSkuProducts;
    }
  }

  private getFilteredProducts(products: LineDeivceDetails[], sku: string): LineDeivceDetails[] {
    return products.filter((device: LineDeivceDetails) => {
      return this.objectService.get(device, 'selectedDevice.sku') === sku;
    });
  }

  /**
   * Method to get Line by id
   * @param lines : Lines present
   * @param id :selected Line id
   * @returns CartLine
   */
  private getLineById(lines: CartLine[], id: number): CartLine {
    return this.objectService.get(lines, id.toString());
  }
}
